/**
 * Parcels
 */

// React
import React, { FunctionComponent, useEffect, useState, useContext } from 'react';

// Context
import AuthContext from '../../authContext';

// Redux
import { connect } from 'react-redux';
import { compose } from 'redux';
import {
  getParcelsAction,
  updateParcelsActions,
  createParcelsActions,
  updateParcelsStatusActions,
  deleteParcelAction,
} from './actions';
import { Store } from '../../redux/reducers';

// Selector
import { createStructuredSelector } from 'reselect';
import {
  makeSelectList,
  makeSelectLoading,
  makeSelectError,
  makeSelectCountList,
} from './selectors';

// Components
import Pagination from '@material-ui/lab/Pagination';
import List from './components/list';

import RenderFilter from '../../utils/listDaynamicFilterHookForm';
import { useForm } from 'react-hook-form';

// UI
import theme from '../../theme';
import clsx from 'clsx';
import { makeStyles, createStyles, Theme } from '@material-ui/core/styles';
import {
  Typography,
  CircularProgress,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  Grid,
  DialogTitle,
  IconButton,
  InputBase,
  ClickAwayListener,
  FormControl,
  Select,
  MenuItem, TextField,
} from '@material-ui/core';

// Icon
import Icon from '@mdi/react';
import {
  mdiArrowRight,
  mdiMagnify,
  mdiCheck,
  mdiClose,
  mdiCalendar,
  mdiMapMarkerOutline,
  mdiMapMarker,
  mdiKeyOutline,
  mdiStateMachine, mdiCollage, mdiPencilOutline,
} from '@mdi/js';

// Helper
import { removeEmptyValueObject, translateParcelsStatus } from '../../utils/helpers';

// Type
import { Props, SelectorProps, PayloadUpdate, PayloadCreate } from './interfaces';
import type { Filter } from '../../utils/listDaynamicFilter';
import { Status } from '../warehouseAdminParcelsReceipt/interfaces';
import useQuery from '../../utils/hook/useQuery';
type Inputs = {
  'id.equals': string;
};

// Style
const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    root: {},
    paper: {},
    loading: {
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      height: '50vh',
      alignSelf: 'center',
      justifyContent: 'center',
      background: '#ccc',
    },
    pagination: {
      // marginTop: theme.spacing(1),
    },
    textArea: {
      width: '100%',
    },
    searchBox: {
      display: 'flex',
      position: 'relative',
      backgroundColor: theme.palette.common.white,
      borderRadius: '4px',
      padding: '10px 0',
      boxShadow: '0px 2px 4px 1px rgba(0,0,0,0.1)',
    },

    input: {
      marginLeft: theme.spacing(1),
      flex: 2,
      '@media (max-width: 768px)': {
        fontSize: '12px',
      },
    },
    iconButton: {
      padding: 10,
      marginRight: '10px',
      position: 'relative',
      '&:before': {
        content: '""',
        position: 'absolute',
        width: '1px',
        height: '36px',
        top: '4px',
        left: '-9px',
        backgroundColor: '#EDF1F4',
      },
    },
    filterBox: {
      position: 'absolute',
      backgroundColor: '#fff',
      width: '100%',
      top: '47px',
      right: '0',
      marginTop: '30px',
      borderRadius: '0  0 4px  4px',
      padding: '16px 16px',
      boxSizing: 'border-box',
      boxShadow: '0px 2px 4px 1px rgba(0,0,0,0.1)',
      zIndex: 10,
    },

    filterOpen: {
      display: 'block',
    },
    filterClose: {
      display: 'none',
    },
    paginationBox: {
      display: 'flex',
    },
    selectSize: {
      width: '35px',
    },
    subtitle: {
      fontSize: '14px',
      fontWeight: 500,
      color: '#8A8A8A',
    },
    magnifyIcon: {
      transform: 'rotate(-90deg)',
    },
    // Cancel Dialog
    cancelIcon: {
      textAlign: 'center',
      position: 'relative',
      paddingLeft: theme.spacing(8),
      marginBottom: theme.spacing(7),
      '&::before': {
        content: '""',
        position: 'absolute',
        width: '100px',
        top: '50%',
        left: '50%',
        transform: 'translate(-50%,-50%)',
        height: '100px',
        borderRadius: '50%',
        opacity: '0.2',
        backgroundColor: theme.palette.error.main,
      },
    },
    cancelTitle: {
      fontSize: '16px',
      fontWeight: 500,
      textAlign: 'center',
      marginBottom: theme.spacing(3),
    },
    actionsBox: {
      paddingTop: theme.spacing(2),
      paddingRight: theme.spacing(3),
      paddingLeft: theme.spacing(3),
    },
    editIcon: {
      textAlign: 'center',
      position: 'relative',
      paddingLeft: theme.spacing(8),
      // marginBottom: theme.spacing(7),
      '&::before': {
        content: '""',
        position: 'absolute',
        width: '100px',
        top: '50%',
        left: '50%',
        transform: 'translate(-50%,-50%)',
        height: '100px',
        borderRadius: '50%',
        opacity: '0.2',
        backgroundColor: theme.palette.blue.main,
      },
    },
    addIcon: {
      textAlign: 'center',
      position: 'relative',
      paddingLeft: theme.spacing(8),
      // marginBottom: theme.spacing(7),
      '&::before': {
        content: '""',
        position: 'absolute',
        width: '100px',
        top: '50%',
        left: '50%',
        transform: 'translate(-50%,-50%)',
        height: '100px',
        borderRadius: '50%',
        opacity: '0.2',
        backgroundColor: theme.palette.green.main,
      },
    },
    closeButton: {
      position: 'absolute',
      right: theme.spacing(1),
      top: theme.spacing(1),
      color: theme.palette.grey[500],
    },
  })
);

const Parcels: FunctionComponent<Props> = (props: Props) => {
  const { register, handleSubmit } = useForm<Inputs>();
  const authContext = useContext(AuthContext);
  const [openFilterBox, setOpenFilterBox] = useState(false as boolean);

  const { filtersValues, setFiltersValues } = useQuery({
    page: 0,
    size: 10,
  } as any);
  const [filters, setFilters] = useState([] as Filter[]);
  const [openConfirmDisable, setOpenConfirmDisable] = useState(
    false as boolean
  );
  const [openDialog, setOpenDialog] = useState(false as boolean);
  const [type, setType] = useState('edit' as string);

  const [openAdd, setOpenAdd] = useState(false as boolean);
  const [id, setId] = useState(-1 as number);
  const [openConfirmActive, setOpenConfirmActive] = useState(false as boolean);
  const [openSize, setOpenSize] = React.useState(false as boolean);
  const [previewState, setPreviewState] = useState('CANCELLED' as any);
  const [openConfirmUpdateState, setOpenConfirmUpdateState] = useState(
    false as boolean
  );
  const [rejectDescription, setRejectDescription] = useState('');

  const filterTranslator = (fieldName: string) => {
    const status = {
      sourceWarehouseName: 'مبدا',
      'sourceWarehouseName.equals': 'مبدا:',
      'sourceWarehouseName.contains': 'مبدا:',
      destinationWarehouseName: 'مقصد',
      'destinationWarehouseName.equals': 'مقصد',
      'destinationWarehouseName.contains': 'مقصد',
      'id.equals': 'شناسه محموله:',
      'id.contains': 'شناسه محموله:',
      shippingDate: 'تاریخ ثبت',
      'shippingDate.equals': 'تاریخ ثبت:',
      'shippingDate.contains': 'تاریخ ثبت:',
      deliveryDate: 'تاریخ تحویل',
      'deliveryDate.equals': 'تاریخ تحویل:',
      'deliveryDate.contains': 'تاریخ تحویل:',
      items: 'تعدادکارتن',
      'items.equals': 'تعدادکارتن:',
      'items.contains': 'تعدادکارتن:',
      status: 'وضعیت',
      'status.in': 'وضعیت:',
      'status.notIn': 'وضعیت:',
    };
    return status[fieldName];
  };
  const removeFilterHandler = (fieldName: any) => {
    const filtered = { ...filtersValues };
    delete filtered[fieldName];
    setFiltersValues(filtered);
  };

  useEffect(() => {
    getParcels();
    setupFilters();
  }, [filtersValues]); // eslint-disable-line react-hooks/exhaustive-deps
  const classes = useStyles();
  const handleChangePage = (
    event: React.ChangeEvent<unknown>,
    page: number
  ) => {
    setFiltersValues({ ...filtersValues, page: page - 1 });
  };

  const getParcels = () => {
    const { page, size } = filtersValues;
    // const {id} = props.match.params;
    props.parcelsDispatch({
      ...removeEmptyValueObject(filtersValues),
      // id: {equals: id},
      page,
      size,
    });
  };

  const handleChangeSizePage = (event: React.ChangeEvent<{ value: unknown }>) => {
    setFiltersValues({ ...filtersValues, page: 0, size: event.target.value });
  };

  const handleOpen = (id: number, type: string) => {
    setType(type);
    setOpenDialog(true);
    setId(id);
  };

  const handleClose = () => {
    setOpenConfirmDisable(false);
    setOpenDialog(false);
    setOpenAdd(false);
    setOpenConfirmActive(false);
    setOpenSize(false);
  };

  const onSubmit = (filterValues: any) => {
    setFiltersValues({
      ...removeEmptyValueObject(filterValues),
      page: 0,
      size: 10,
    });
    setOpenFilterBox(false);
  };

  const handleClickAway = (e: any) => {
    // TODO: Shet
    if (
      e.target.getAttribute('dir') !== 'rtl' &&
      e.target.getAttribute('type') !== 'checkbox' &&
      e.target.getAttribute('class') !== 'PrivateSwitchBase-input-73' &&
      e.target.getAttribute('class') !==
      'MuiTypography-root MuiListItemText-primary MuiTypography-body1 MuiTypography-displayBlock' &&
      e.target.getAttribute('aria-hidden') !== 'true'
    )
      setOpenFilterBox(false);
  };

  const handleClick = () => {
    setOpenFilterBox(prev => !prev);
  };

  const setupFilters = () => {
    setFilters([
      {
        type: 'input',
        id: 'sourceWarehouseName.contains',
        label: 'مبدا',
        icon: mdiMapMarkerOutline,
      },
      {
        type: 'input',
        id: 'destinationWarehouseName.contains',
        label: 'مقصد',
        icon: mdiMapMarker,
      },
      {
        type: 'input',
        id: 'id.equals',
        label: 'شناسه محموله ',
        icon: mdiKeyOutline,
      },
      {
        type: 'input',
        id: 'operatorId.equals',
        label: 'شناسه اپراتور ',
        icon: mdiKeyOutline,
      },
      {
        type: 'number',
        id: 'boxCount.equals',
        label: 'تعداد کارتن ',
        icon: mdiCollage,
      },
      {
        type: 'checkbox',
        id: 'status.in',
        label: 'وضعیت محموله',
        data: ['SUBMITTED', 'SHIPPED', 'DELIVERED', 'CANCELLED'],
        translateType: 'parcel',
        icon: mdiStateMachine,
      },
      {
        type: 'date-picker',
        id: 'shippingDate.greaterThanOrEqual',
        label: 'تاریخ ثبت محموله از ',
        icon: mdiCalendar,
      },
      {
        type: 'date-picker',
        id: 'deliveryDate.lessThanOrEqual',
        label: 'تا تاریخ ثبت محموله ',
        icon: mdiCalendar,
      },
      {
        type: 'date-picker',
        id: 'deliveryDate.greaterThanOrEqual',
        label: 'تاریخ تحویل از ',
        icon: mdiCalendar,
      },
      {
        type: 'date-picker',
        id: 'deliveryDate.lessThanOrEqual',
        label: 'تا تاریخ تحویل',
        icon: mdiCalendar,
      },
    ]);
  };
  const headCells = [
    {
      id: 'partnerName',
      label: 'نام شرکت',
      type: 'input',
    },
    {
      id: 'sourceWarehouseName',
      label: 'مبدا',
      type: 'input',
    },
    {
      id: 'destinationWarehouseName',
      label: 'مقصد',
      type: 'input',
    },
    {
      id: 'id',
      label: 'شناسه محموله',
      type: 'input',
    },
    {
      id: 'shippingDate',
      label: 'تاریخ ثبت',
      type: 'date-picker',
    },
    {
      id: 'deliveryDate',
      label: 'تاریخ تحویل',
      type: 'date-picker',
    },
    {
      id: 'items',
      label: 'تعدادکارتن',
      type: 'input',
    },
    {
      id: 'status',
      label: 'وضعیت',
      type: 'checkbox',
      data: [
        'SUBMITTED',
        'SHIPPED',
        'DELIVERED',
        'CANCELLED',
        'LOCATED',
        'REGISTERED',
        'ASSIGNED',
      ],
    },
  ];
  const head = { headCells };
  const handleDisableUser = async () => {
    await props.updateParcelsStatusDispatch({
      id,
      body: 'CANCELLED'.replace(/'/g, '"'),
      pagination: filtersValues,
    });
    setOpenConfirmDisable(false);
  };

  const handleOpenConfirmDisable = (id: number) => {
    setOpenConfirmDisable(true);
    setId(id);
  };
  const handleOpenConfirmActive = (id: number) => {
    setOpenConfirmActive(true);
    setId(id);
  };

  const handleOpenConfirmUpdateState = (data: { id: number; body: any }) => {
    setId(data.id);
    setOpenConfirmUpdateState(true);
    setPreviewState(data.body);
  };

  const closeConfirmUpdateState = () => {
    setOpenConfirmUpdateState(false);
  };

  const findStatus = (id: number) => {
    const status = props.parcelsList.find(parcel => parcel.id === id)
      ?.status as Status;
    return status && translateParcelsStatus(status);
  };

  const changeStatusParcel = () => {
    const { warehouseId } = authContext.user;
    const params = warehouseId
      ? {
        destinationWarehouseId: { equals: warehouseId },
      }
      : { ...filtersValues };

    const desc = previewState === 'RETURNING' ? rejectDescription : '';

    props.updateParcelsStatusDispatch({
      id,
      body: { status: previewState, description: desc },
      pagination: params,
    });
    setOpenConfirmUpdateState(false);
  };



  return (
    <div className={classes.root}>
      <Grid container spacing={6} alignItems="center">
        <Grid container item xs={12} sm={6} alignItems="center">
          <Typography variant="h4" component="h2">
            {`لیست محموله‌ها `}
          </Typography>
        </Grid>

        <Grid item sm={6} xs={12}>
          <RenderFilter
            filters={filters}
            onFilter={(filtervalues: any) => {
              setFiltersValues({
                ...removeEmptyValueObject(filtervalues),
                page: 0,
                size: 10,
              });
            }}
            mainFiltersCount={0}
            baseFilter={{
              type: 'input',
              id: 'id.equals',
              placeholder: 'شناسه محموله',
            }}
            filterValues={filtersValues}
          />
        </Grid>
      </Grid>
      <Grid container spacing={3} style={{ marginTop: '30px' }}>
        <Grid item xs={12}>
          {props.loading ? (
            <div className={classes.loading}>
              <CircularProgress />
            </div>
          ) : (
            <>
              {props.parcelsList?.length > 0 ? (
                <List
                  data={props.parcelsList}
                  handleOpenConfirmDisable={handleOpenConfirmDisable}
                  handleOpenConfirmActive={handleOpenConfirmActive}
                  handleOpen={handleOpen}
                  changeStatus={handleOpenConfirmUpdateState}
                  head={head}
                  filterVal={filtersValues}
                  onFilterValue={(filtervalues: any) => {
                    setFiltersValues({
                      ...removeEmptyValueObject(filtervalues),
                      ...filtersValues,
                      page: 0,
                      size: 10,
                    });
                  }}
                  removeFilterHandler={removeFilterHandler}
                  filterLabelTranslator={filterTranslator}
                />
              ) : (
                <Typography variant="h3" component="div">
                  محموله موجود نیست
                </Typography>
              )}
            </>
          )}
        </Grid>
        <Grid item xs={12} sm={6}>
          {props.parcelsList.length > 0 && (
            <FormControl
              style={{
                display: 'flex',
                flexDirection: 'row',
                alignItems: 'center',
              }}
            >
              <div style={{ marginLeft: '10px', color: '#8a8a8a' }}>
                {' '}
                تعداد نمایش در هر صفحه:
              </div>
              <Select
                open={openSize}
                onClose={handleClose}
                onOpen={() => setOpenSize(true)}
                value={filtersValues.size}
                onChange={handleChangeSizePage}
              >
                <MenuItem value={10}>10</MenuItem>
                <MenuItem value={20}>20</MenuItem>
                <MenuItem value={50}>50</MenuItem>
                <MenuItem value={100}>100</MenuItem>
              </Select>
            </FormControl>
          )}
        </Grid>
        <Grid xs={12} sm={6} container item direction="row-reverse">
          {props.parcelsList.length > 0 && (
            <Pagination
              className={classes.pagination}
              count={Math.ceil(props.count / filtersValues.size)}
              color="secondary"
              page={filtersValues.page + 1}
              onChange={handleChangePage}
            />
          )}
        </Grid>
      </Grid>

      {/* <Dialog
        fullWidth
        open={openConfirmDisable}
        onClose={handleClose}
        aria-labelledby="responsive-dialog-title"
      >
        <DialogTitle>
          {` آیا از غیر فعال کردن انبار با نام
          "${props.warehousesList.find(item => item.id === id)?.name}"
          اطمینان دارید؟`}
        </DialogTitle>
        <DialogActions>
          <Button
            autoFocus
            onClick={handleClose}
            variant="contained"
            color="primary"
          >
            {`خیر`}
          </Button>
          <Button
            onClick={handleDisableUser}
            variant="outlined"
            color="primary"
          >
            {`بله`}
          </Button>
        </DialogActions>
      </Dialog> */}
      <Dialog
        fullScreen
        open={openDialog}
        onClose={handleClose}
        aria-labelledby="responsive-dialog-title"
      >
        <DialogTitle>
          {/* <div>{`
      گزارش موجودی انبار
        "${props.parcelsList.find(item => item.id === id)?.name}"
        `}</div> */}
          <Button aria-label="details" size="medium" onClick={handleClose}>
            <Icon path={mdiArrowRight} size={1} />
            {`بازگشت`}
          </Button>
        </DialogTitle>
        <DialogContent>
          {/* {type === 'edit' && (
            <Edit
              data={props.parcelsList.find(item => item.id === id)}
              partnerId={props.match.params.id}
              update={body => {
                props.updateParcelsDispatch({
                  body,
                  pagination: filtersValues,
                  id: props.match.params.id,
                });
                handleClose();
              }}
            />
          )} */}
        </DialogContent>
      </Dialog>

      <Dialog
        fullWidth
        open={openConfirmDisable || openConfirmActive}
        onClose={handleClose}
        aria-labelledby="responsive-dialog-title"
      >
        <DialogTitle>
          <div
            style={{
              position: 'absolute',
              left: '17px',
              zIndex: 10,
            }}
          >
            {handleClose ? (
              <IconButton
                aria-label="close"
                // className={classes.closeButton}
                onClick={handleClose}
              >
                <Icon path={mdiClose} size={1} />
              </IconButton>
            ) : null}
          </div>
          <div
            className={
              openConfirmDisable ? classes.cancelIcon : classes.editIcon
            }
          >
            {openConfirmDisable ? (
              <Icon path={mdiClose} size={4} color={theme.palette.error.main} />
            ) : (
              <Icon path={mdiCheck} size={4} color={theme.palette.blue.main} />
            )}
          </div>
          <div className={classes.cancelTitle}>
            {` از 
            
            لغو محموله با شناسه
          "${props.parcelsList.find(item => item.id === id)?.id}"
          مطمئن هستید؟`}
          </div>
        </DialogTitle>
        <DialogActions>
          <Grid container spacing={1}>
            <Grid item xs={8}>
              <Button
                fullWidth
                autoFocus
                onClick={handleClose}
                variant="contained"
                color="secondary"
                size="large"
              >
                {`خیر`}
              </Button>
            </Grid>

            <Grid item xs={4}>
              <Button
                onClick={handleDisableUser}
                fullWidth
                variant="contained"
                size="large"
              >
                {`بله`}
              </Button>
            </Grid>
          </Grid>
        </DialogActions>
      </Dialog>

      <Dialog
        fullWidth
        open={openConfirmUpdateState}
        onClose={closeConfirmUpdateState}
        aria-labelledby="responsive-dialog-title"
      >
        <DialogContent>
          <div
            style={{
              position: 'absolute',
              left: '17px',
              zIndex: 10,
            }}
          >
            {closeConfirmUpdateState ? (
              <IconButton
                aria-label="close"
                // className={classes.closeButton}
                onClick={closeConfirmUpdateState}
              >
                <Icon path={mdiClose} size={1} />
              </IconButton>
            ) : null}
          </div>
          <div className={classes.editIcon}>
            <Icon
              path={mdiPencilOutline}
              size={4}
              color={theme.palette.blue.main}
            />
          </div>
          <div className={classes.cancelTitle}>
            {`از تغییر تگ وضعیت محموله
                با شناسه  ${id} از
 "${id > -1 && findStatus(id)}"
 به
" ${translateParcelsStatus(previewState.replace(/"/g, '') as Status)}"
 مطمئن هستید؟`}
          </div>
          {
            previewState === 'RETURNING' ? (<TextField
              className={classes.textArea}
              placeholder={`علت ریجکت...`}
              value={rejectDescription}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                setRejectDescription(e.target.value)
              }
            />) : null
          }
        </DialogContent>
        <DialogActions className={classes.actionsBox}>
          <Grid container spacing={2}>
            <Grid item xs={8}>
              <Button
                fullWidth
                autoFocus
                onClick={closeConfirmUpdateState}
                variant="contained"
                color="secondary"
                size="large"
              >
                {'خیر'}
              </Button>
            </Grid>
            <Grid item xs={4}>
              <Button
                fullWidth
                variant="contained"
                onClick={changeStatusParcel}
                size="large"
              >
                {'بله'}
              </Button>
            </Grid>
          </Grid>
        </DialogActions>
      </Dialog>
    </div>
  );
};

const mapStateToProps = createStructuredSelector<Store, SelectorProps>({
  parcelsList: makeSelectList(),
  count: makeSelectCountList(),
  loading: makeSelectLoading(),
  error: makeSelectError(),
});

function mapDispatchToProps(dispatch: Function) {
  return {
    parcelsDispatch: (payload: Object) => dispatch(getParcelsAction(payload)),
    updateWarehouseDispatch: (payload: PayloadUpdate) =>
      dispatch(updateParcelsActions(payload)),
    createParcelsDispatch: (payload: PayloadCreate) =>
      dispatch(createParcelsActions(payload)),
    deleteParcelDispatch: (payload: { id: number }) =>
      dispatch(deleteParcelAction(payload)),
    updateParcelsStatusDispatch: (payload: {
      id: number;
      body: string;
      pagination: object;
    }) => dispatch(updateParcelsStatusActions(payload)),
  };
}

const withConnect = connect(mapStateToProps, mapDispatchToProps);
export default compose(withConnect)(Parcels);
